import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarCheckbox,
  ToolbarSpacer,
  ToolbarButton,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip, toneFromMuiColor } from '../components/chip';
import type {
  Todo,
  TodoStatus,
  TodoPriority,
  TodoBillingResult,
  CreateTodoRequest,
  UpdateTodoRequest,
  InvoiceScheduleCreateRequest,
} from '@techgitter/contracts';
import { fetchTodos, createTodo, updateTodo, deleteTodo, type TodoFilters } from '../services/todosApi';
import { createInvoiceSchedule } from '../services/invoiceScheduleAdminService';
import { getCustomerIndex } from '../services/customerIndexApi';
import { useUsers } from '../hooks/useUsers';
import { ToDoTaskPanel } from '../components/admin/ToDoTaskPanel';
import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  statusLabel,
  statusColor,
  priorityLabel,
  priorityColor,
  formatDueDate,
} from '../utils/todoDisplay';

export const ToDoTasksAdminPage: React.FC = () => {
  const [todos, setTodos] = useState<Todo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<TodoStatus | ''>('');
  const [filterPriority, setFilterPriority] = useState<TodoPriority | ''>('');
  const [showDone, setShowDone] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Billing: offer to create a monthly schedule when the customer has none
  const [billingOffer, setBillingOffer] = useState<{ billing: TodoBillingResult; todo: Todo } | null>(null);
  const [creatingSchedule, setCreatingSchedule] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { users } = useUsers();

  const filters = useMemo<TodoFilters>(() => ({
    assignedTo: filterAssignee || undefined,
    status: filterStatus || undefined,
    priority: filterPriority || undefined,
    includeDone: showDone || undefined,
  }), [filterAssignee, filterStatus, filterPriority, showDone]);

  const loadTodos = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchTodos(filters, signal);
      setTodos(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load to-do tasks');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    loadTodos(controller.signal);
    return () => controller.abort();
  }, [loadTodos]);

  const moneyFmt = (n: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n);

  // React to a billing result returned when a task is completed.
  const processBillingResult = (billing: TodoBillingResult | undefined, todo: Todo) => {
    if (!billing) return;
    const item = billing.item;
    const customerName = todo.customer?.name || 'customer';
    if (billing.status === 'billed') {
      const amount = item ? moneyFmt((item.priceRetail || 0) * (item.quantity || 1)) : '';
      setSnackbar({
        open: true,
        message: `Added ${amount} one-time charge "${item?.name}" to ${customerName}'s monthly invoice schedule. View under Admin → Schedules → Invoices.`,
        severity: 'success',
      });
    } else if (billing.status === 'error') {
      setSnackbar({ open: true, message: `Billing failed: ${billing.message ?? 'unknown error'}`, severity: 'error' });
    } else if (billing.status === 'needs-schedule') {
      setBillingOffer({ billing, todo });
    }
  };

  const handleCreateScheduleFromOffer = async () => {
    if (!billingOffer) return;
    const { billing, todo } = billingOffer;
    const item = billing.item;
    const customerDvId = billing.customerId;
    if (!item || !customerDvId) {
      setBillingOffer(null);
      return;
    }
    setCreatingSchedule(true);
    try {
      // Resolve the customer's RepairShopr id (schedule create needs both ids).
      const index = await getCustomerIndex();
      const match = index.items.find((c) => c.dataverseCustomerId === customerDvId);
      const payload: InvoiceScheduleCreateRequest = {
        customer: { dataverseId: customerDvId, repairShoprId: match?.repairShoprCustomerId ?? '' },
        name: `${todo.customer?.name ?? 'Customer'} Monthly`,
        frequency: 'Monthly',
        lineItems: [{
          name: item.name,
          quantity: item.quantity,
          priceRetail: item.priceRetail,
          description: item.description,
          oneTimeCharge: true,
        }],
      };
      await createInvoiceSchedule(payload);
      setSnackbar({
        open: true,
        message: `Created a monthly invoice schedule for ${todo.customer?.name ?? 'customer'} with the one-time charge.`,
        severity: 'success',
      });
      setBillingOffer(null);
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to create schedule', severity: 'error' });
    } finally {
      setCreatingSchedule(false);
    }
  };

  const handleCreate = () => {
    setEditingTodo(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleEdit = (todo: Todo) => {
    setEditingTodo(todo);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async (payload: CreateTodoRequest | UpdateTodoRequest) => {
    setIsSaving(true);
    setDialogError(null);
    try {
      let billingResult: TodoBillingResult | undefined;
      const wasEditing = editingTodo;
      if (wasEditing) {
        const res = await updateTodo(wasEditing.id, payload);
        billingResult = res?.billing;
      } else {
        await createTodo(payload as CreateTodoRequest);
      }
      setDialogOpen(false);
      setEditingTodo(null);
      setSnackbar({
        open: true,
        message: wasEditing ? 'To-do updated' : 'To-do created',
        severity: 'success',
      });
      loadTodos();
      if (wasEditing && billingResult) {
        // Use the merged view (payload reflects the latest customer/billing).
        processBillingResult(billingResult, { ...wasEditing, customer: wasEditing.customer });
      }
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInlineStatusChange = async (todo: Todo, status: TodoStatus) => {
    const previous = todo.status;
    setTodos((current) => current.map((t) => (t.id === todo.id ? { ...t, status } : t)));
    try {
      const res = await updateTodo(todo.id, { status });
      if (status === 'done') {
        processBillingResult(res?.billing, todo);
      }
      // A task moved to 'done' drops out of the default (hide-done) view.
      if (status === 'done' && !showDone) {
        setTodos((current) => current.filter((t) => t.id !== todo.id));
      }
    } catch (err) {
      setTodos((current) => current.map((t) => (t.id === todo.id ? { ...t, status: previous } : t)));
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update status',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await deleteTodo(deleteConfirmId);
      setTodos((current) => current.filter((t) => t.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'To-do deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete',
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteTarget = todos.find((t) => t.id === deleteConfirmId);

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Assignee"
          value={filterAssignee}
          onChange={setFilterAssignee}
          options={[{ value: '', label: 'All' }, ...users.map((u) => ({ value: u.guid, label: u.fullname || u.email }))]}
        />
        <ToolbarSelect
          label="Status"
          value={filterStatus}
          onChange={(v) => setFilterStatus(v as TodoStatus | '')}
          options={[{ value: '', label: 'All' }, ...STATUS_OPTIONS]}
        />
        <ToolbarSelect
          label="Priority"
          value={filterPriority}
          onChange={(v) => setFilterPriority(v as TodoPriority | '')}
          options={[{ value: '', label: 'All' }, ...PRIORITY_OPTIONS]}
        />
        <ToolbarCheckbox label="Show done" active={showDone} onClick={() => setShowDone((v) => !v)} />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => loadTodos()}>
          Refresh
        </ToolbarButton>
        <ToolbarButton variant="primary" icon={IconPlus} onClick={handleCreate}>
          Add To-Do
        </ToolbarButton>
      </SecondaryToolbar>

      {/* Two-column layout — content left, To-Do panel right. Collapses to a
          single column when the panel is closed. */}
      <div className={`todo-layout ${dialogOpen ? 'todo-layout--panel-open' : 'todo-layout--panel-closed'}`}>
      <Box sx={{ p: '12px 16px 24px', minWidth: 0 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Title</DataTableHeadCell>
                <DataTableHeadCell>Assignee</DataTableHeadCell>
                <DataTableHeadCell>Priority</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell>Scheduled</DataTableHeadCell>
                <DataTableHeadCell>Due</DataTableHeadCell>
                <DataTableHeadCell>Customer</DataTableHeadCell>
                <DataTableHeadCell>Work Item</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {todos.length === 0 ? (
                <DataTableEmpty colSpan={9}>No to-do tasks</DataTableEmpty>
              ) : (
                todos.map((todo) => (
                  <DataTableRow key={todo.id}>
                    <DataTableCell>
                      <Typography variant="body2" fontWeight="bold">{todo.title}</Typography>
                      {todo.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
                          {todo.description}
                        </Typography>
                      )}
                      {todo.outputKind === 'ticket' && (
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ display: 'block', maxWidth: 280, color: todo.ticketError && !todo.ticketMessageId ? '#b03a2e' : '#4A90D9' }}
                        >
                          {todo.ticketMessageId
                            ? `Ticket raised${todo.ticketNumber ? ` — #${todo.ticketNumber}` : ''}`
                            : todo.ticketError
                              ? 'Raising the ticket failed — retried next run'
                              : 'Raises a ticket on its scheduled date'}
                        </Typography>
                      )}
                    </DataTableCell>
                    <DataTableCell>{todo.assignedToName || '—'}</DataTableCell>
                    <DataTableCell>
                      <Chip tone={toneFromMuiColor(priorityColor(todo.priority))}>{priorityLabel(todo.priority)}</Chip>
                    </DataTableCell>
                    <DataTableCell>
                      <Select
                        value={todo.status}
                        onChange={(e) => handleInlineStatusChange(todo, e.target.value as TodoStatus)}
                        size="small"
                        variant="standard"
                        disableUnderline
                        renderValue={(value) => (
                          <Chip tone={toneFromMuiColor(statusColor(value as TodoStatus))}>{statusLabel(value as TodoStatus)}</Chip>
                        )}
                        sx={{ '& .MuiSelect-select': { p: 0, pr: '20px !important' } }}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </DataTableCell>
                    <DataTableCell>{formatDueDate(todo.scheduledDate)}</DataTableCell>
                    <DataTableCell>{formatDueDate(todo.dueDate)}</DataTableCell>
                    <DataTableCell>{todo.customer?.name || '—'}</DataTableCell>
                    <DataTableCell>
                      {todo.workItem
                        ? `${todo.workItem.number ? `#${todo.workItem.number} ` : ''}${todo.workItem.title ?? ''}`.trim() || '—'
                        : '—'}
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(todo)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirmId(todo.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
      </Box>

      {dialogOpen && (
        <div className="todo-panel-col">
          <ToDoTaskPanel
            todo={editingTodo}
            isSaving={isSaving}
            error={dialogError}
            onClose={() => {
              if (!isSaving) {
                setDialogOpen(false);
                setEditingTodo(null);
                setDialogError(null);
              }
            }}
            onSave={handleSave}
          />
        </div>
      )}
      </div>

      <Dialog open={deleteConfirmId !== null} onClose={isDeleting ? undefined : () => setDeleteConfirmId(null)}>
        <DialogTitle>Delete To-Do Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.title}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={billingOffer !== null} onClose={creatingSchedule ? undefined : () => setBillingOffer(null)}>
        <DialogTitle>No monthly invoice schedule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {billingOffer?.todo.customer?.name || 'This customer'} has no monthly invoice schedule, so the
            one-time charge{billingOffer?.billing.item ? ` "${billingOffer.billing.item.name}"` : ''} couldn&apos;t be added.
            Create a monthly schedule prepopulated with this charge?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillingOffer(null)} disabled={creatingSchedule}>Cancel</Button>
          <Button onClick={handleCreateScheduleFromOffer} variant="contained" disabled={creatingSchedule}>
            {creatingSchedule ? 'Creating...' : 'Create schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
